// © Microsoft Corporation. All rights reserved.

import { AppError, logger } from "@msnews/diagnostics";
import { BaseExperience, InitialCommonExperienceProps } from "@msnews/experiences-base";
import { BridgeEventNames, handleBridgeEvent } from "./Social.bridge";
import { HandledPropsKeys, TelemetryBase } from "@msnews/core";
import { SocialHandledProps, SocialUnhandledProps } from "./Social.props";
import { TelemetryEventNames, convertToHybridTags, handleEvent } from "./Social.telemetry";

import { ExperienceIds } from "@msnews/experiences-constants";
import Main from "./main/main";
import Popup from "./popup/Popup";
import React from "react";
import { RenderErrors } from "./Social.errors";
import { SocialService } from "@msnews/social-data-service";
import { TelemetryDOM } from "@msnews/telemetry-core";
import { appErrorLogger } from "./utils/AppErrorLogger";
import { canUseDOM } from "@msnews/isomorphic-util";
import { enums } from "./utils/enums";
import { getContextData } from "@msnews/core";

declare global {
    interface Window {
        loginWindow: any;
        trackFromWidget: any;
        sapphireBridge: any;
        msaLoginWindow: any;
        signalR: any;
        auth2: any;
        googleInit: any;
        gapi: any;
        FB: any;
        fbAsyncInit: any;
        listenMSALoginWindow: any;
        msaLogin: any;
        clipboardData: any;
        AzureStorage: any;
        require: (r: any, f: any) => void;
        ReactNativeWebView: any;
    }
}

/**
 * Starter kit experience
 */
export class Social extends BaseExperience<SocialHandledProps, SocialUnhandledProps, {}> {

    private responsiveClass = "";

    /** The Social default prop contract. Describes any default values for any component handled props */
    public static defaultProps: Partial<SocialHandledProps> = {
        experienceName: ExperienceIds.social
    };
    private handleTelemetryCall: Function;
    private handlePartialPageImpressionCall: Function;
    private handleAppErrorEventCall: (AppError) => void;

    constructor(props, context) {
        super(props, context);

        this.handleTelemetryCall = this.props.handleTelemetryCall;
        this.handlePartialPageImpressionCall = this.props.handlePartialPageImpressionCall;
        this.handleAppErrorEventCall = this.props.handleAppErrorEventCall;
        if (this.handleAppErrorEventCall) {
            appErrorLogger.setOnAppError(this.handleAppErrorEventCall);
        }
    }

    /** @inheritdoc */
    protected handledProps: HandledPropsKeys<SocialHandledProps> = {
        ...InitialCommonExperienceProps,
        checkAndLogReducerError: undefined
    };

    /** @inheritdoc */
    public componentWillMount(): void {
        try {
            const locationHref = getContextData(this.props.requestContext.data.locationHref, "locationHref");
            const ppeEnvironmentSelected = locationHref.toLowerCase().indexOf(enums.selectPpeParameter) > -1;
            if (ppeEnvironmentSelected) { SocialService.setDomain(enums.externalUrls.ppeApi); }
        } catch (e) {
            logger.log("Error occured using requestContext.");
        }
    }

    /** @ineritdoc */
    public componentDidMount(): void {
        this.setupTelemetry();
        this.setupBridge();
        this.listenMediaQueries();
    }

    /** @inheritdoc */
    public componentWillUnmount(): void {
        this.teardownTelemetry();
        this.teardownBridge();
    }

    public componentDidCatch(error, info): void {
        const componentRegex = new RegExp("in (.+) .created by");
        const componentNameResult = info.componentStack.match(componentRegex);
        let componentName = componentNameResult && componentNameResult[1] || "unknown";
        const reduxResult = componentName.match(/Connect\((.+)\)/);
        const isRedux = (reduxResult && reduxResult[1]) != null;
        if (isRedux) {
            componentName = reduxResult[1];
        }
        const isPopup = info.componentStack.indexOf("created by Popup") > 0;
        const appError = RenderErrors[componentName] || RenderErrors.UnknownComponent;

        const { moduleName, id } = this.props;

        appErrorLogger.log({
            ...appError,
            message: "Social experience component failed to render",
            pb: {
                ...appError.pb,
                customMessage: componentName + " " + error.name + ": " + error.message,
                stack: error.stack,
                socialProps: { moduleName, id },
                isPopup,
                isRedux
            }
        } as AppError);
    }

    public componentDidUpdate() {
        this.props.checkAndLogReducerError();
    }

    /** @inheritdoc */
    public render(): JSX.Element {
        super.render();

        const { t, theme, children } = this.props;
        const propsToPass = {...this.unhandledProps(), children, responsiveClass: this.responsiveClass, markTtvr: this.markTtvr, t, theme};
        return (
            <React.Fragment>
                <Main {...propsToPass} requestContext={this.props.requestContext} />
                <Popup {...propsToPass} requestContext={this.props.requestContext} />
            </React.Fragment>
        );
    }

    /** Non nav event handler */
    private telemetryHandler = (e: CustomEvent) => {
        if (e.detail.consumed) { return; }

        e.detail.consumed = true;

        try {
            if (!e.detail.element) { return; }

            const element = e.detail.element as HTMLElement;
            const telemetryEvent = e.detail.telemetryEvent;
            const refreshDataAttribute = e.detail.refreshDataAttribute;

            if ((!element.hasAttribute("data-t") && !element.hasAttribute("data-m")) || refreshDataAttribute) {
                const elementTelemetryObject = this.props.mainTelemetryObject.addOrUpdateChild({...telemetryEvent.contract});
                const telemetryDomElement = TelemetryDOM.get(element);
                telemetryDomElement ? telemetryDomElement.isVisible = true : null;

                const customTelemetryTags = convertToHybridTags(elementTelemetryObject);
                // Render Hybrid tags
                if (customTelemetryTags) {
                    TelemetryBase.setTrackingAttrs(element, element.parentElement, {defaults: customTelemetryTags});
                } else {
                // Render Peregrine tags
                    const telemetryTagsObject = elementTelemetryObject.render();
                    // Add other attributes if there are other than data-t
                    Object.keys(telemetryTagsObject).map(key => {
                        element.setAttribute(key, telemetryTagsObject[key]);
                    });
                }
            }

            handleEvent(telemetryEvent, element, this.handleTelemetryCall, this.handlePartialPageImpressionCall);
        } catch (e) {
            // Suppress the error
        }
    };

    private setupTelemetry = () => {
        if (canUseDOM()) {
            Object.keys(TelemetryEventNames).forEach(eventName => document.addEventListener(TelemetryEventNames[eventName], this.telemetryHandler));
        }
    };

    private setupBridge = () => {
        if (canUseDOM()) {
            Object.keys(BridgeEventNames).forEach(eventName => document.addEventListener(BridgeEventNames[eventName], handleBridgeEvent));
        }
    };

    private teardownTelemetry = () => {
        if (canUseDOM()) {
            Object.keys(TelemetryEventNames).forEach(eventName => document.removeEventListener(TelemetryEventNames[eventName], this.telemetryHandler));
        }
    };

    private teardownBridge = () => {
        if (canUseDOM()) {
            Object.keys(BridgeEventNames).forEach(eventName => document.removeEventListener(BridgeEventNames[eventName], handleBridgeEvent));
        }
    };

    /** Helper to mark time to visually ready */
    private markTtvr = () => {
        window.requestAnimationFrame(() => {
            setTimeout(() => {
                super.markTimeToVisuallyReady(true);
            }, 0);
        });
    };

    /** CSS Media Queries */
    listenMediaQueries() {
        const queryLevels = [
            {
                query: window.matchMedia("(max-width: 640px)"),
                className: enums.responsiveLevels.L0
            },
            {
                query: window.matchMedia("(max-width: 540px) and (max-aspect-ratio: 1/1)"),
                className: enums.responsiveLevels.MobilePopup
            },
            {
                query: window.matchMedia("(max-width: 480px)"),
                className: enums.responsiveLevels.L1
            },
            {
                query: window.matchMedia("(max-width: 340px)"),
                className: enums.responsiveLevels.L2
            }
        ];

        // Add listeners for each query level
        queryLevels.map(queryLevel => queryLevel.query.addListener(() => this.checkMediaQueryLevel(queryLevel)));

        // Check all queryLevels after the next event tick
        setTimeout(() => {
            queryLevels.map(queryLevel => this.checkMediaQueryLevel(queryLevel));
        });
    }

    checkMediaQueryLevel(queryLevel) {
        let classNames= (this.responsiveClass || "").split(" ");

        if (queryLevel.query.matches) {
            classNames.push(queryLevel.className);
        } else {
            classNames = classNames.filter(className => className != queryLevel.className);
        }

        this.responsiveClass = classNames.join(" ");

        if (this.props.styleConnector) {
            this.props.styleConnector.updateResponsiveClass(this.responsiveClass);
        }
    }
}